import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs/internal/Observable';
import { MatDialog } from '@angular/material/dialog';
import { filter, flatMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationModalComponent } from '../../../../dialogs/confirmation-modal/confirmation-modal.component';
import { DepositTickerRuleService } from '../deposit-ticker-rule.service';
import { DepositActionRuleDTO, LimitType } from '../deposit-ticker-action.model';

@Component({
  selector: 'app-deposit-ticker-single-rule',
  templateUrl: './deposit-ticker-single-rule.component.html',
  styleUrls: ['./deposit-ticker-single-rule.component.scss'],
  standalone: false,
})
export class DepositTickerSingleRuleComponent implements OnInit {
  @Input() data: DepositActionRuleDTO | undefined;

  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  @Output() updated: EventEmitter<DepositActionRuleDTO> = new EventEmitter<DepositActionRuleDTO>();

  @Output() deleted: EventEmitter<DepositActionRuleDTO> = new EventEmitter<DepositActionRuleDTO>();

  LimitType = LimitType;

  formGroup: UntypedFormGroup | undefined;

  editMode = false;

  constructor(
    private fb: UntypedFormBuilder,
    private service: DepositTickerRuleService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initialize();
  }

  private initialize() {
    this.editMode = !this.data;
    this.formGroup = this.fb.group({
      id: [this.data?.id],
      minFraudScoreType: [this.data?.minFraudScoreType || LimitType.NO_LIMIT],
      minFraudScore: [this.data?.minFraudScore],
      maxFraudScoreType: [this.data?.maxFraudScoreType || LimitType.NO_LIMIT],
      maxFraudScore: [this.data?.maxFraudScore],
    });
    if (!this.editMode) {
      this.formGroup.disable();
    }
  }

  cancel(): void {
    this.formGroup.disable();
    this.initialize();
    this.cancelled.emit();
  }

  save(): void {
    const func: () => Observable<DepositActionRuleDTO> = this.data ? () => this.update() : () => this.insert();
    func().subscribe((r) => {
      this.updated.emit(r);
      this.toastr.success(`Rule ${r.id} ${this.data ? 'updated' : 'inserted'}`, null, { timeOut: 5000 });
    });
  }

  delete(): void {
    this.dialog
      .open(ConfirmationModalComponent, { data: { note: `Are you sure you want to delete rule #${this.data.id}?` } })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        flatMap(() => this.service.delete(this.data.id))
      )
      .subscribe(() => {
        this.deleted.emit();
        this.toastr.success(`Rule ${this.data.id} deleted`, null, { timeOut: 5000 });
      });
  }

  insert(): Observable<DepositActionRuleDTO> {
    return this.service.insert(this.formToDTO());
  }

  private formToDTO(): DepositActionRuleDTO {
    const formValue = this.formGroup.getRawValue();
    return {
      id: this.data?.id,
      minFraudScoreType: formValue.minFraudScoreType as LimitType,
      minFraudScore: formValue.minFraudScoreType == LimitType.NO_LIMIT ? null : formValue.minFraudScore,
      maxFraudScoreType: formValue.maxFraudScoreType as LimitType,
      maxFraudScore: formValue.maxFraudScoreType == LimitType.NO_LIMIT ? null : formValue.maxFraudScore,
    };
  }

  update(): Observable<DepositActionRuleDTO> {
    return this.service.update(this.formToDTO());
  }

  startEditing(): void {
    this.editMode = true;
    this.formGroup.enable();
  }
}
